import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

type DashboardInfoTipProps = {
  helpKey: string;
  size?: 'small' | 'medium';
  scope?: 'dashboard' | 'report';
};

export const DashboardInfoTip = ({ helpKey, size = 'small', scope = 'dashboard' }: DashboardInfoTipProps) => {
  const { t } = useTranslation();
  const text = t(`common:${scope}.help.${helpKey}`);

  return (
    <Tooltip
      title={
        <Typography variant="body2" sx={{ maxWidth: 320, lineHeight: 1.45, py: 0.25 }}>
          {text}
        </Typography>
      }
      arrow
      placement="top"
      enterTouchDelay={0}
    >
      {/* component="span" so this can sit inside ToggleButton / other <button>s without nested buttons */}
      <IconButton
        component="span"
        size="small"
        role="button"
        tabIndex={0}
        aria-label={text}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
        sx={{
          p: 0.25,
          color: 'text.secondary',
          display: 'inline-flex',
          verticalAlign: 'middle',
          '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />
      </IconButton>
    </Tooltip>
  );
};

type DashboardSectionTitleProps = {
  title: string;
  helpKey: string;
  subtitle?: string;
  mb?: number;
  endAdornment?: React.ReactNode;
};

export const DashboardSectionTitle = ({
  title,
  helpKey,
  subtitle,
  mb = 2,
  endAdornment,
}: DashboardSectionTitleProps) => (
  <Box sx={{ mb: subtitle ? 0.5 : mb }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <DashboardInfoTip helpKey={helpKey} />
      {endAdornment}
    </Box>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

type DashboardMetricLabelProps = {
  label: string;
  helpKey: string;
};

export const DashboardMetricLabel = ({ label, helpKey }: DashboardMetricLabelProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontWeight: 500,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </Typography>
    <DashboardInfoTip helpKey={helpKey} size="small" />
  </Box>
);